import { useState } from 'react';
import { Users, Plus, Trash2, Loader2, Crown, Shield, Edit, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { useProducerMembers } from '@/hooks/useProducerMembers';
import { ProducerMemberRole } from '@/data/producers/members';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface ProducerTeamSectionProps {
  producerId: string;
  currentUserId?: string;
}

const ROLE_LABELS: Record<ProducerMemberRole, string> = {
  admin: 'Ägare / Medvärd',
  editor: 'Redigerare',
  viewer: 'Läsare',
};

const ROLE_ICONS: Record<ProducerMemberRole, React.ReactNode> = {
  admin: <Shield className="h-4 w-4" />,
  editor: <Edit className="h-4 w-4" />,
  viewer: <Eye className="h-4 w-4" />,
};

const ROLE_COLORS: Record<ProducerMemberRole, string> = {
  admin: 'bg-blue-100 text-blue-800 border-blue-200',
  editor: 'bg-green-100 text-green-800 border-green-200',
  viewer: 'bg-muted text-muted-foreground border-border',
};

export function ProducerTeamSection({ producerId, currentUserId }: ProducerTeamSectionProps) {
  const { members, isLoading, error, addMember, removeMember, refresh } = useProducerMembers(producerId);
  const { toast } = useToast();

  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<ProducerMemberRole>('viewer');
  const [isAdding, setIsAdding] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{ userId: string; email: string } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Check if current user can manage members (owner or admin)
  const currentUserMember = members.find((m) => m.user_id === currentUserId);
  const canManageMembers = currentUserMember?.role === 'admin';

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newEmail.trim()) {
      toast({
        title: 'E-post saknas',
        description: 'Ange en e-postadress.',
        variant: 'destructive',
      });
      return;
    }

    setIsAdding(true);
    try {
      await addMember(newEmail.trim(), newRole);
      toast({
        title: 'Medlem tillagd',
        description: `${newEmail} har lagts till som ${ROLE_LABELS[newRole].toLowerCase()}.`,
      });
      setNewEmail('');
      setNewRole('viewer');
    } catch (err: any) {
      toast({
        title: 'Kunde inte lägga till',
        description: err.message || 'Något gick fel. Försök igen.',
        variant: 'destructive',
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;

    setIsRemoving(true);
    try {
      await removeMember(memberToRemove.userId);
      toast({
        title: 'Medlem borttagen',
        description: `${memberToRemove.email} har tagits bort.`,
      });
      setMemberToRemove(null);
    } catch (err: any) {
      toast({
        title: 'Kunde inte ta bort',
        description: err.message || 'Något gick fel. Försök igen.',
        variant: 'destructive',
      });
    } finally {
      setIsRemoving(false);
    }
  };

  if (isLoading) {
    return (
      <section className="space-y-6">
        <div>
          <h2 className="text-lg font-medium text-foreground flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Hantera vem som har tillgång till denna producent.
          </p>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="space-y-6">
        <div>
          <h2 className="text-lg font-medium text-foreground flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team
          </h2>
        </div>
        <div className="p-4 rounded-lg border border-destructive/30 bg-destructive/10">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={refresh} className="mt-2">
            Försök igen
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-foreground flex items-center gap-2">
          <Users className="h-5 w-5" />
          Team
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Hantera vem som har tillgång till denna producent.
        </p>
      </div>

      {/* Members list */}
      <div className="space-y-2">
        {members.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            Inga teammedlemmar än.
          </p>
        ) : (
          members.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between p-3 rounded-lg border border-border bg-card"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                  {member.email.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {member.email}
                    {member.user_id === currentUserId && (
                      <span className="text-muted-foreground ml-1">(du)</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Medlem sedan {new Date(member.created_at).toLocaleDateString('sv-SE')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={cn('flex items-center gap-1', ROLE_COLORS[member.role])}
                >
                  {ROLE_ICONS[member.role]}
                  {ROLE_LABELS[member.role]}
                </Badge>
                {canManageMembers && member.user_id !== currentUserId && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => setMemberToRemove({ userId: member.user_id, email: member.email })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add member form */}
      {canManageMembers && (
        <form onSubmit={handleAddMember} className="space-y-4 pt-4 border-t border-border">
          <Label className="text-sm font-medium">Lägg till teammedlem</Label>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="email"
              placeholder="E-postadress"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="flex-1"
            />
            <Select value={newRole} onValueChange={(v) => setNewRole(v as ProducerMemberRole)}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="viewer">Läsare</SelectItem>
                <SelectItem value="editor">Redigerare</SelectItem>
                <SelectItem value="admin">Administratör</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" disabled={isAdding}>
              {isAdding ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Lägg till
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Användaren måste ha ett konto i systemet. Inbjudningar via e-post kommer snart.
          </p>
        </form>
      )}

      {/* Remove confirmation dialog */}
      <AlertDialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <AlertDialogContent className="bg-background">
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort teammedlem?</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill ta bort {memberToRemove?.email} från teamet?
              Användaren kommer inte längre ha tillgång till denna producent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              disabled={isRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemoving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ta bort'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
